import {
  IAdminPharmacyWorkshiftsListData,
  ICalendarEventType,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  ServiceTypes,
  TimeFormat,
  TypeOfUser,
  WorkshiftReportStatus,
  WorkshiftStatus,
} from '@pharmaplan/common';
import ServiceHandler, {
  idStore,
} from '@pharmaplan/common/helpers/ServiceHandler';
import strings from '../../../../localization';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import { timeString } from '../../../../helpers/Functions';
import {
  DynamicTableCell,
  OtherScreens,
  PharmacyWorkshiftStatusLabels,
} from '../../../../helpers/Constants';
import {
  convertDecimal,
  handleValue,
} from '../../Profile/Pharmacy/PharmacyViewProfile/helper';
import { IUserProfileLinkSignature } from '../../../../hooks/Admin/useUserProfileLink';
import deleteRequestIcon from '../../../../assets/svg/delete.svg';
import requestCancellationIcon from '../../../../assets/svg/requestCancellation.svg';
import editWorkshift from '../../../../assets/svg/edit.svg';
import findMatchingIcon from '../../../../assets/svg/findMatching.svg';
import expressBookingIcon from '../../../../assets/svg/expressBooking.svg';
import { dateRecord, rateRecord } from '../../../../helpers/longTermRecord';
import {
  ILongTermDeleteDetails,
  IShowCancellationParams,
} from '../../../../hooks/Admin/useWorkshiftDelete';

const { text, customLink, checkbox } = DynamicTableCell;

const isSelfService = ServiceTypes.self === ServiceHandler.getService();

interface IListOfWorkshiftHeader {
  key: string;
  label: string;
  sortingKey?: string;
  isCenterAligned?: boolean;
}

interface IPharmacyWorkshiftListRow {
  data: IAdminPharmacyWorkshiftsListData[];
  timeFormat: TimeFormat;
  goToUserProfile: IUserProfileLinkSignature;
  showWorkshiftDelete: (
    workshiftId: string,
    dateTime: string,
    params?: ILongTermDeleteDetails
  ) => void;
  showRequestCancellation: ({
    bookingId,
    dateTime,
    longTermShiftContractId,
    professionalId,
  }: IShowCancellationParams) => void;
  findMatchingAvailabilities: (workshiftId: string) => void;
  showExpressBooking: (workshiftId: string) => void;
  canDelete: boolean;
  isPast: boolean;
  handleEdit: (
    type: ICalendarEventType,
    bookingId: string | null,
    workshiftId: string
  ) => () => void;
  selectedItems?: Array<string>;
  handlePress: (id: string, count?: number) => () => void;
}

export const pharmacyWorkshiftsListHeaders = () =>
  [
    {
      key: '0',
      label: `${strings.date} | ${strings.time}`,
      sortingKey: 'startDate',
    },
    { key: '1', label: strings.status, sortingKey: 'status' },
    {
      key: '2',
      label: strings.totalHours,
      isCenterAligned: true,
      sortingKey: 'totalHours',
    },
    { key: '3', label: strings.replacementName, sortingKey: 'pharmacistName' },
    !isSelfService && {
      key: '4',
      label: strings.pharmacyHourlyRate,
      isCenterAligned: true,
      sortingKey: 'pharmacyHourlyRate',
    },
    !isSelfService && {
      key: '5',
      label: strings.pharmacistHourlyRate,
      isCenterAligned: true,
      sortingKey: 'paidHourlyRate',
    },
    { key: '6', label: strings.action },
  ].filter(Boolean) as Array<IListOfWorkshiftHeader>;

export const pastWorkshiftsListHeaders = () =>
  [
    {
      key: '0',
      label: `${strings.date} | ${strings.time}`,
      sortingKey: 'startDate',
    },
    { key: '1', label: strings.status, sortingKey: 'status' },
    {
      key: '2',
      label: strings.totalHours,
      isCenterAligned: true,
      sortingKey: 'totalHours',
    },
    { key: '5', label: strings.replacementName, sortingKey: 'pharmacistName' },
    !isSelfService && {
      key: '3',
      label: strings.pharmacyHourlyRate,
      isCenterAligned: true,
      sortingKey: 'pharmacyHourlyRate',
    },
    {
      key: '4',
      label: strings.pharmacistHourlyRate,
      isCenterAligned: true,
      sortingKey: 'paidHourlyRate',
    },
  ].filter(Boolean) as Array<IListOfWorkshiftHeader>;

export const statusMap = {
  [WorkshiftStatus.Posted]: strings.posted,
  [WorkshiftStatus.Requested]: strings.requested,
  [WorkshiftStatus.Booked]: strings.booked,
  [WorkshiftStatus.Unfulfilled]: strings.unfulfilled,
};

export const pharmacyWorkshiftsListRow = ({
  data,
  timeFormat,
  goToUserProfile,
  showWorkshiftDelete,
  showRequestCancellation,
  findMatchingAvailabilities,
  handleEdit,
  showExpressBooking,
  canDelete,
  isPast,
  selectedItems,
  handlePress,
}: IPharmacyWorkshiftListRow) =>
  data.map((item) => {
    const {
      id,
      bookingId,
      status,
      endDate,
      startDate,
      pharmacistName,
      type,
      workShiftId,
      totalHours,
      paidHourlyRate,
      pharmacyHourlyRate,
      professionalName,
      externalPharmacistId,
      longTermShiftContractId,
      totalShiftCount = 0,
      bookedShiftCount = 0,
      weekDayPaidHourlyRate = 0,
      weekendPaidHourlyRate = 0,
      pharmacyWeekDayHourlyRate = 0,
      pharmacyWeekendHourlyRate = 0,
    } = item ?? {};

    const date = getDayMonthDateYear(startDate);
    const time = timeString(startDate, endDate, timeFormat);
    const postedCount = totalShiftCount - bookedShiftCount;
    const userId = item[idStore.id as keyof typeof item] as string;

    const dateTime = `${date} | ${time}`;

    const isBooked = type === PharmacyLegendVariant.booked;
    const isPosted = type === PharmacyLegendVariant.posted;
    const isRequested = type === PharmacyLegendVariant.requested;

    const name = isSelfService ? professionalName : pharmacistName;
    const isExternal = !!externalPharmacistId;

    const handleOnClick = () => {
      if (isBooked && (bookingId)) {
        showRequestCancellation({
          bookingId: bookingId ?? '',
          dateTime,
          longTermShiftContractId,
          professionalId: userId,
        });
      } else {
        showWorkshiftDelete(workShiftId, dateTime, {
          postedCount,
          isLongTerm: !!longTermShiftContractId,
        });
      }
    };

    return {
      key: id,
      data: [
        dateRecord(
          { startDate, endDate, timeFormat },
          PharmacistLegendVariant.workshift,
          longTermShiftContractId,
        ),
        {
          key: '1',
          value: PharmacyWorkshiftStatusLabels()[status],
          type: text,
        },
        {
          key: '2',
          value: totalHours,
          type: text,
          isCenterAligned: true,
        },
        !isBooked || isExternal
          ? {
            key: '3',
            value: handleValue(name ?? ''),
            type: text,
          }
          : {
            key: '11',
            value: {
              label: name,
              handlePress: goToUserProfile({
                personnelId: userId,
                userType: TypeOfUser.pharmacist,
                fromScreen: OtherScreens.ListOfWorkshifts,
              }),
            },
            type: customLink,
          },
        !isSelfService && {
          ...rateRecord(
            {
              weekDayPaidHourlyRate: pharmacyWeekDayHourlyRate,
              weekendPaidHourlyRate: pharmacyWeekendHourlyRate,
              rate: pharmacyHourlyRate,
            },
            longTermShiftContractId,
          ),
          key: '11~',
          isCenterAligned: true,
        },
        !isSelfService && {
          ...rateRecord(
            {
              weekDayPaidHourlyRate,
              weekendPaidHourlyRate,
              rate: paidHourlyRate,
            },
            longTermShiftContractId,
          ),
          isCenterAligned: true,
        },

        !isPast && {
          key: '7',
          value: [
            isPosted
              && !isExternal
              && !longTermShiftContractId && {
              key: '11',
              tooltip: strings.expressBooking,
              icon: expressBookingIcon,
              onClick: () => {
                showExpressBooking(workShiftId);
              },
            },
            !isExternal && {
              key: '9',
              tooltip: isRequested ? strings.expressBooking : strings.edit,
              icon: isRequested ? expressBookingIcon : editWorkshift,
              onClick: handleEdit(
                longTermShiftContractId ? PharmacyLegendVariant.posted : type,
                bookingId,
                workShiftId,
              ),
            },
            !(isBooked || isExternal || longTermShiftContractId) && {
              key: '8',
              tooltip: strings.matchingAvailabilities,
              icon: findMatchingIcon,
              onClick: () => {
                findMatchingAvailabilities(workShiftId);
              },
            },
            canDelete && {
              key: '10',
              tooltip: isBooked ? strings.cancel : strings.delete,
              icon: isBooked ? requestCancellationIcon : deleteRequestIcon,
              onClick: handleOnClick,
            },
          ],
          type: DynamicTableCell.bar,
        },
        !isPast
          && canDelete && {
          key: '8',
          value: {
            checked: !!selectedItems?.includes(workShiftId),
            handlePress: handlePress(workShiftId, postedCount),
            disabled: !isPosted,
            tooltipText: PharmacyWorkshiftStatusLabels()[status],
          },
          type: checkbox,
        },
      ].filter(Boolean),
    };
  });
